import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  $createRangeSelection,
  $isParagraphNode,
  LexicalNode,
  $getNodeByKey,
} from 'lexical';
import { $isListNode, $isListItemNode } from '@lexical/list';
import { $createIxbrlNode, type IXBRLNodeProps, $isXbrlNode, IXBRLNode } from './IXBRLNode';

const findAncestorListNode = (node: LexicalNode) => {
  let parent = node.getParent();
  while (parent) {
    if ($isListNode(parent)) {
      return parent;
    }
    parent = parent.getParent();
  }
  return null;
};

type WrapParagraphProps = IXBRLNodeProps & {
  selectedNodes: LexicalNode[];
  elementType?: string;
}

function $wrapMultipleBlocks({ selectedNodes, ...props }: WrapParagraphProps) {
  // Group selected inline nodes by their parent block node (paragraph, listitem, etc.)
  const listsToWrap = new Set<string>();
  const blockMap = new Map<string, LexicalNode[]>();

  for (const node of selectedNodes) {
    const ancestorList = findAncestorListNode(node);
    if (ancestorList) {
      listsToWrap.add(ancestorList.getKey());
      continue;
    }

    // Only wrap inline nodes (text, linebreak, etc.), skip block nodes
    if ($isParagraphNode(node) || $isListItemNode(node) || $isListNode(node)) continue;

    const parent = node.getParent();
    if (parent && $isParagraphNode(parent)) {
      const key = parent.getKey();
      if (!blockMap.has(key)) {
        blockMap.set(key, []);
      }
      blockMap.get(key)!.push(node);
    }
  }

  // For each block, wrap its selected inline nodes in an IXBRL node
  let firstIxbrlNode: IXBRLNode | null = null;
  let lastIxbrlNode: IXBRLNode | null = null;

  for (const listKey of listsToWrap) {
    const listNode = $getNodeByKey(listKey);
    if (!$isListNode(listNode) || (listNode.getParent() && $isXbrlNode(listNode.getParent()))) {
      continue;
    }

    const ixbrlNode = $createIxbrlNode({ ...props, elementType: 'div' });
    listNode.insertBefore(ixbrlNode);
    ixbrlNode.append(listNode);

    if (!firstIxbrlNode) {
      firstIxbrlNode = ixbrlNode;
    }
    lastIxbrlNode = ixbrlNode;
  }

  for (const [_blockKey, inlineNodes] of blockMap.entries()) {
    if (inlineNodes.length === 0) continue;
    const ixbrlNode = $createIxbrlNode(props);

    const firstInline = inlineNodes[0];
    firstInline.insertBefore(ixbrlNode);

    const nodesToMove = [...inlineNodes];
    for (const node of nodesToMove) {
      ixbrlNode.append(node);
    }

    if (!firstIxbrlNode) firstIxbrlNode = ixbrlNode;
    lastIxbrlNode = ixbrlNode;
  }

  return { firstIxbrlNode, lastIxbrlNode };
}

function $wrapSingleBlock({ selectedNodes, ...props }: WrapParagraphProps) {
  const wrapperNode = $createIxbrlNode(props);
  const firstSelected = selectedNodes[0];

  firstSelected.insertBefore(wrapperNode);

  // Move selected nodes into the wrapper, unless already wrapped
  for (const node of selectedNodes) {
    const parent = node.getParent();

    if (parent && $isXbrlNode(parent)) {
      console.warn('[$wrapSelectionInIxbrlNode] Skipping node already inside IXBRLNode:', node.getKey());
      continue;
    }

    wrapperNode.append(node);
  }

  return wrapperNode;
}

/**
 * Lexical-style utility to wrap current selection in a new IXBRLNode.
 * Skips nodes already wrapped and safely handles nested selections.
 */
export function $wrapSelectionInIxbrlNode(props: IXBRLNodeProps): void {
  const selection = $getSelection();

  if (!$isRangeSelection(selection)) {
    console.warn('[$wrapSelectionInIxbrlNode] Aborting: not a range selection');
    return;
  }

  const [anchor, focus] = [selection.anchor, selection.focus].sort((a, b) => {
    return a.getNode().getKey() < b.getNode().getKey() ? -1 : 1;
  });

  const anchorNode = anchor.getNode();
  const focusNode = focus.getNode();

  if (!anchorNode || !focusNode) {
    console.warn('[$wrapSelectionInIxbrlNode] Aborting: missing anchor or focus node');
    return;
  }

  // Split partial text nodes at selection boundaries
  if ($isTextNode(anchorNode) && anchor.offset > 0) {
    anchorNode.splitText(anchor.offset);
  }

  if ($isTextNode(focusNode) && focus.offset < focusNode.getTextContentSize()) {
    focusNode.splitText(focus.offset);
  }

  const selectedNodes = selection.getNodes();
  if (selectedNodes.length === 0) {
    console.warn('[$wrapSelectionInIxbrlNode] Aborting: no selected nodes found');
    return;
  }

  const blockCount = selectedNodes.filter((node) => $isParagraphNode(node) || $isListItemNode(node)).length;
  const hasMultipleBlocks = blockCount > 1;
  const params = { ...props, selectedNodes };
  let firstElement, lastElement;

  if (hasMultipleBlocks) {
    const { firstIxbrlNode, lastIxbrlNode } = $wrapMultipleBlocks(params);
    firstElement = firstIxbrlNode;
    lastElement = lastIxbrlNode;
  } else {
    const wrapperNode = $wrapSingleBlock(params);
    firstElement = wrapperNode;
    lastElement = wrapperNode.getChildren().at(-1) ?? wrapperNode;
  }

  // Restore selection
  if (firstElement && lastElement) {
    const newSelection = $createRangeSelection();
    newSelection.anchor.set(firstElement.getKey(), 0, 'element');
    if ($isElementNode(lastElement)) {
      newSelection.focus.set(lastElement.getKey(), lastElement.getTextContentSize(), 'element');
    } else {
      newSelection.focus.set(lastElement.getKey(), lastElement.getTextContentSize(), 'text');
    }
    $setSelection(newSelection);
  }
}
